// @flow
/* eslint-disable no-nested-ternary */

import * as React from 'react';

import { createValidator, required } from 'gandi.react-form';
import type { TranslatorProps } from '@gandi/translate/dist/types';

import type { Props as ContactSupportFormProps } from './ContactSupportForm/components/ContactSupportForm';

type LocaleProps = {|
  locale: string,
|};

type Props = {|
  ...ContactSupportFormProps,
  ...LocaleProps,
  translator: TranslatorProps,
|};

type DataProps = {|
  requiredIfIsForFqdn: boolean,
  requiredIfIsForIp: boolean,
  requiredIfIsNotFeedback: boolean,
  requiredIfIsCorporate: boolean,
  requiredIfIsMail: boolean,
  requiredIfMigration: boolean,
  email: string,
  handle: string,
  fqdn: string,
  minCharacters: string,
|};

const requiredIfIsForFqdn = (value: string, data: DataProps, props: Props) => {
  const { isNotForFqdn } = props;
  if (!isNotForFqdn) {
    return required(value, data, props);
  }
  return undefined;
};

const requiredIfIsForIp = (value: string, data: DataProps, props: Props) => {
  const { isForIp } = props;
  if (isForIp) {
    return required(value, data, props);
  }
  return undefined;
};

const requiredIfIsNotFeedback = (
  value: string,
  data: DataProps,
  props: Props
) => {
  const { routeParams } = props;
  if (routeParams.subject !== 'feedback') {
    return required(value, data, props);
  }
  return undefined;
};

const requiredIfIsCorporate = (
  value: string,
  data: DataProps,
  props: Props
) => {
  const { routeParams } = props;
  if (routeParams.subject === 'corporate-contact') {
    return required(value, data, props);
  }
  return undefined;
};

const requiredIfIsMail = (value: string, data: DataProps, props: Props) => {
  const { routeParams } = props;

  if (
    routeParams.topic === 'mail-sending-problems' ||
    routeParams.topic === 'mail-receiving-problems'
  ) {
    return required(value, data, props);
  }
  return undefined;
};

const requiredIfMigration = (value: string, data: DataProps, props: Props) => {
  const { routeParams } = props;
  if (routeParams.subject === 'migration') {
    return required(value, data, props);
  }
  return undefined;
};

const handle = (value: string, data: DataProps, props: Props) => {
  const suffix = '-GANDI';
  // doesn't end with suffix
  if (
    value &&
    value.toUpperCase().indexOf(suffix, value.length - suffix.length) === -1
  ) {
    return props.translator.__('Must be a Gandi handle (AB1234-GANDI)');
  }

  return undefined;
};

const emailFormat = /.+@[^@]+\.[^@]{2,}/;

const email = (value: string, data: DataProps, props: Props) => {
  if (value && !emailFormat.test(value)) {
    return props.translator.__('Must be a valid E-mail address');
  }

  return undefined;
};

const fqdn = (value: string, data: DataProps, props: Props) => {
  if (value && value.indexOf('.') === -1) {
    return props.translator.__('Must be a valid domain name');
  }

  return undefined;
};

const minCharacters = (value: string, data: DataProps, props: Props) => {
  const isStandaloneForm = !!(
    props.routeParams.subject && props.routeParams.subject.match(/-/)
  );

  const minChar =
    props.locale === 'ja' || isStandaloneForm
      ? undefined
      : props.locale === 'zh-hans' || props.locale === 'zh-hant'
      ? 20
      : 100;

  if (!minChar) {
    return undefined;
  }

  if (value && value.length < minChar) {
    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: props.translator.__(
            "<p>I'm sorry, we need more information from you.<br/>" +
              'So we can provide you with the best possible reply:</p>' +
              '<ul><li>clearly state the issue you are having, </li>' +
              '<li>tell us what you are trying to do, and where,</li>' +
              '<li>any error message you are getting.</li></ul>' +
              '<p>The more information you provide to us now, the better we can ' +
              'examine your situation and avoid asking you for more information.</p>'
          ),
        }}
      />
    );
  }

  return undefined;
};

const contactSupportValidation: any = createValidator({
  sub_category: [required],
  firstname: [requiredIfIsNotFeedback],
  lastname: [requiredIfIsNotFeedback],
  orgname: [requiredIfIsCorporate],
  reply_to: [required, email],
  params: {
    domain: [requiredIfIsForFqdn, fqdn],
    handle: [requiredIfMigration, handle],
    ip: [requiredIfIsForIp],
    phone: [requiredIfIsCorporate],
    error_start_date: [requiredIfIsMail],
    application_used: [requiredIfIsMail],
    error_type: [requiredIfIsMail],
    error_message: [requiredIfIsMail],
  },
  message: [required, minCharacters],
});

export default contactSupportValidation;
